import React, { useState, useEffect, useRef } from 'react';
import { useParams } from 'react-router-dom';
import axios from 'axios';
import { useLanguage } from '../contexts/LanguageContext';
import Navbar from '../components/Navbar';
import LanguageToggle from '../components/LanguageToggle';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const ChatPage = () => {
  const { mentorType } = useParams();
  const { t } = useLanguage();
  const [messages, setMessages] = useState([]);
  const [inputMessage, setInputMessage] = useState('');
  const [loading, setLoading] = useState(false);
  const [conversationId, setConversationId] = useState(null);
  const [discipline, setDiscipline] = useState('');
  const [examContext, setExamContext] = useState('');
  const [selectedImage, setSelectedImage] = useState(null);
  const [imagePreview, setImagePreview] = useState(null);
  const messagesEndRef = useRef(null);
  const fileInputRef = useRef(null);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  const getMentorTitle = () => {
    const titles = {
      general: t('general_mentor'),
      physics: t('physics_mentor'),
      math: t('math_mentor'),
      chemistry: t('chemistry_mentor'),
      biology: t('biology_mentor')
    };
    return titles[mentorType] || t('general_mentor');
  };

  const handleImageSelect = (e) => {
    const file = e.target.files[0];
    if (file) {
      setSelectedImage(file);
      const reader = new FileReader();
      reader.onloadend = () => {
        setImagePreview(reader.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleRemoveImage = () => {
    setSelectedImage(null);
    setImagePreview(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const handleSendMessage = async (e) => {
    e.preventDefault();
    if (!inputMessage.trim() && !selectedImage) return;

    // Check usage limit before sending
    try {
      const limitCheck = await axios.post(
        `${API}/subscriptions/check-limit`,
        { action_type: 'mentor_message' },
        { withCredentials: true }
      );

      if (!limitCheck.data.allowed) {
        const reason = limitCheck.data.reason;
        if (reason === 'daily_limit_reached') {
          alert(language === 'tr' 
            ? `⚠️ Günlük mentor mesaj limitinize ulaştınız (${limitCheck.data.limit}). Yarın tekrar deneyin veya planınızı yükseltin.`
            : `⚠️ Daily mentor message limit reached (${limitCheck.data.limit}). Try again tomorrow or upgrade your plan.`);
        } else if (reason === 'monthly_limit_reached') {
          alert(language === 'tr'
            ? `⚠️ Aylık mentor mesaj limitinize ulaştınız (${limitCheck.data.limit}). Planınızı yükseltin.`
            : `⚠️ Monthly mentor message limit reached (${limitCheck.data.limit}). Please upgrade your plan.`);
        }
        return;
      }
    } catch (error) {
      console.error('Error checking limit:', error);
      // Continue anyway if limit check fails
    }

    const userMessage = {
      role: 'user',
      content: inputMessage,
      image: imagePreview,
      timestamp: new Date().toISOString()
    };

    setMessages(prev => [...prev, userMessage]);
    const messageText = inputMessage;
    const imageFile = selectedImage;
    
    setInputMessage('');
    setSelectedImage(null);
    setImagePreview(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
    setLoading(true);

    try {
      let response;
      
      if (imageFile) {
        // Send with image using FormData
        const formData = new FormData();
        formData.append('image', imageFile);
        formData.append('message', messageText || 'Bu soruyu çözmeme yardım edebilir misin?');
        formData.append('mentor_type', mentorType);
        if (conversationId) formData.append('conversation_id', conversationId);
        if (discipline) formData.append('discipline', discipline);
        if (examContext) formData.append('exam_context', examContext);
        
        response = await axios.post(`${API}/chat/image`, formData, {
          headers: { 'Content-Type': 'multipart/form-data' },
          withCredentials: true
        });
      } else {
        // Text only
        response = await axios.post(`${API}/chat`, {
          conversation_id: conversationId,
          mentor_type: mentorType,
          message: messageText,
          discipline: discipline || undefined,
          exam_context: examContext || undefined
        }, { withCredentials: true });
      }

      if (!conversationId) {
        setConversationId(response.data.conversation_id);
      }

      const assistantMessage = {
        role: 'assistant',
        content: response.data.response,
        timestamp: response.data.timestamp
      };

      setMessages(prev => [...prev, assistantMessage]);

      // Track usage
      try {
        await axios.post(
          `${API}/subscriptions/track-usage`,
          { action_type: 'mentor_message' },
          { withCredentials: true }
        );
      } catch (error) {
        console.error('Error tracking usage:', error);
      }
    } catch (error) {
      console.error('Error sending message:', error);
      const errorMessage = {
        role: 'assistant',
        content: t('error') + ': ' + (error.response?.data?.detail || error.message),
        timestamp: new Date().toISOString()
      };
      setMessages(prev => [...prev, errorMessage]);
    } finally {
      setLoading(false);
    }
  };

  const handleNewChat = () => {
    setMessages([]);
    setConversationId(null);
    setDiscipline('');
    setExamContext('');
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100 flex flex-col">
      <LanguageToggle />
      <Navbar title={getMentorTitle()} />

      <div className="flex-1 max-w-5xl w-full mx-auto px-4 py-6 flex flex-col">
        
        {/* Settings Panel */}
        {messages.length === 0 && (
          <div className="bg-white rounded-2xl shadow-lg p-6 mb-6">
            <h3 className="text-xl font-bold text-gray-800 mb-4">⚙️ {t('exam_context')}</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  {t('discipline')}
                </label>
                <select
                  value={discipline}
                  onChange={(e) => setDiscipline(e.target.value)}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  data-testid="discipline-select"
                >
                  <option value="">{t('select_discipline')}</option>
                  <option value="Physics">{t('physics')}</option>
                  <option value="Mathematics">{t('mathematics')}</option>
                  <option value="Chemistry">{t('chemistry')}</option>
                  <option value="Biology">{t('biology')}</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  {t('exam_context')}
                </label>
                <input
                  type="text"
                  value={examContext}
                  onChange={(e) => setExamContext(e.target.value)}
                  placeholder="AP Physics 1 + TYT/AYT Fizik"
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  data-testid="exam-context-input"
                />
              </div>
            </div>
          </div>
        )}

        {/* Chat Messages */}
        <div className="flex-1 bg-white rounded-2xl shadow-lg overflow-hidden flex flex-col">
          <div className="flex-1 overflow-y-auto p-6 space-y-4" data-testid="chat-messages">
            {messages.length === 0 && (
              <div className="text-center text-gray-500 mt-12">
                <p className="text-lg">👋 {t('type_message')}</p>
              </div>
            )}
            {messages.map((message, index) => (
              <div
                key={index}
                className={`flex ${message.role === 'user' ? 'justify-end' : 'justify-start'}`}
                data-testid={`message-${message.role}`}
              >
                <div
                  className={`max-w-[80%] px-4 py-3 rounded-2xl ${
                    message.role === 'user'
                      ? 'bg-gradient-to-r from-blue-500 to-purple-500 text-white'
                      : 'bg-gray-100 text-gray-800'
                  }`}
                >
                  {message.image && (
                    <img 
                      src={message.image} 
                      alt="Uploaded question" 
                      className="max-w-full rounded-lg mb-2"
                    />
                  )}
                  {message.content && (
                    <div className="whitespace-pre-wrap">{message.content}</div>
                  )}
                </div>
              </div>
            ))}
            {loading && (
              <div className="flex justify-start">
                <div className="bg-gray-100 px-4 py-3 rounded-2xl">
                  <div className="flex space-x-2">
                    <div className="w-2 h-2 bg-gray-500 rounded-full animate-bounce"></div>
                    <div className="w-2 h-2 bg-gray-500 rounded-full animate-bounce" style={{ animationDelay: '0.1s' }}></div>
                    <div className="w-2 h-2 bg-gray-500 rounded-full animate-bounce" style={{ animationDelay: '0.2s' }}></div>
                  </div>
                </div>
              </div>
            )}
            <div ref={messagesEndRef} />
          </div>

          {/* Input Area */}
          <div className="border-t border-gray-200 p-4">
            {/* Image Preview */}
            {imagePreview && (
              <div className="mb-3 relative inline-block">
                <img 
                  src={imagePreview} 
                  alt="Preview" 
                  className="max-h-32 rounded-lg border-2 border-blue-500"
                />
                <button
                  type="button"
                  onClick={handleRemoveImage}
                  className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full w-6 h-6 flex items-center justify-center hover:bg-red-600"
                >
                  ✕
                </button>
              </div>
            )}
            
            <form onSubmit={handleSendMessage} className="flex space-x-2">
              <input
                type="file"
                ref={fileInputRef}
                onChange={handleImageSelect}
                accept="image/*"
                className="hidden"
              />
              <button
                type="button"
                onClick={() => fileInputRef.current?.click()}
                className="bg-gray-200 text-gray-800 p-3 rounded-xl hover:bg-gray-300 transition-colors"
                disabled={loading}
                title={t('upload_image') || 'Resim Yükle'}
              >
                📷
              </button>
              <input
                type="text"
                value={inputMessage}
                onChange={(e) => setInputMessage(e.target.value)}
                placeholder={t('type_message')}
                className="flex-1 px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                disabled={loading}
                data-testid="message-input"
              />
              <button
                type="submit"
                disabled={loading || (!inputMessage.trim() && !selectedImage)}
                className="bg-gradient-to-r from-blue-600 to-purple-600 text-white px-6 py-3 rounded-xl hover:shadow-lg disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-300 font-semibold"
                data-testid="send-button"
              >
                {t('send')}
              </button>
              {messages.length > 0 && (
                <button
                  type="button"
                  onClick={handleNewChat}
                  className="bg-gray-200 text-gray-800 px-6 py-3 rounded-xl hover:bg-gray-300 transition-colors font-semibold"
                  data-testid="new-chat-button"
                >
                  {t('new_chat')}
                </button>
              )}
            </form>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ChatPage;